import React, { useContext, useMemo, useState } from "react";
import dayjs from "dayjs";

import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FolderCopyOutlinedIcon from "@mui/icons-material/FolderCopyOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Slide from "@mui/material/Slide";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import firebase from "firebase";
import { DatabaseContext, UserContext } from "../../context";
import DialogWithTextInput from "../DialogWithTextInput";
import FileListAccordion from "./FileListAccordion";

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

function DeleteEventDialog({ event, open, onClose, onConfirm }) {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xs"
            aria-labelledby="delete-event-dialog-title"
            slotProps={{
                paper: {
                    sx: { borderRadius: 3, overflow: "hidden" },
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    px: { xs: 2, sm: 3 },
                    py: 2.5,
                }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        color: "error.main",
                        backgroundColor: "rgba(211, 47, 47, 0.09)",
                    }}
                >
                    <DeleteOutlineIcon />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        id="delete-event-dialog-title"
                        variant="h5"
                        component="h2"
                        sx={{ lineHeight: 1.1 }}
                    >
                        Delete event
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        title={event?.name}
                        sx={{
                            mt: 0.5,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {event?.name}
                    </Typography>
                </Box>
                <IconButton
                    aria-label="Close delete event confirmation"
                    onClick={onClose}
                    sx={{ mt: -0.75, mr: -1 }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent
                dividers
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    backgroundColor: "background.default",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.25,
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: 2.5,
                        border: "1px solid rgba(211, 47, 47, 0.18)",
                        backgroundColor: "background.paper",
                    }}
                >
                    <WarningAmberOutlinedIcon
                        sx={{ mt: 0.1, flexShrink: 0, color: "error.main" }}
                    />
                    <Box>
                        <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 800 }}
                        >
                            Delete this event permanently?
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.4, lineHeight: 1.55 }}
                        >
                            The event will be removed from its calendar and
                            unlinked from every resource. The resources
                            themselves will not be deleted.
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
                <Button onClick={onClose} color="inherit">
                    Cancel
                </Button>
                <Button
                    onClick={onConfirm}
                    color="error"
                    variant="contained"
                    disableElevation
                    startIcon={<DeleteOutlineIcon />}
                >
                    Delete event
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default function ViewEventDialog({
    event,
    fromFile,
    onClose,
    open,
}) {
    const { files, folders, calendars } = useContext(DatabaseContext);
    const { currentUserData } = useContext(UserContext);
    const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] =
        useState(false);
    const [newNameDialogOpen, setNewNameDialogOpen] = useState(false);

    const calendar = useMemo(
        () => calendars.find((item) => item.key === event?.calendar),
        [calendars, event],
    );

    const linkedFiles = useMemo(
        () =>
            (event?.files || []).map((linkedFile) => {
                const currentFile = files.find(
                    (file) => file.key === linkedFile.key,
                );
                return currentFile
                    ? { ...linkedFile, ...currentFile }
                    : linkedFile;
            }),
        [event, files],
    );

    const userCanEdit = useMemo(() => {
        if (!event || !calendar || !currentUserData) return false;
        if (calendar.user === currentUserData.key) return true;

        const hub = folders.find((folder) => folder.key === calendar.hub);
        return Boolean(hub?.managers?.includes(currentUserData.key));
    }, [calendar, currentUserData, event, folders]);

    const handleConfirmDelete = () => {
        files.forEach((file) => {
            const remainingEventKeys = (file.events || [])
                .filter((linkedEvent) => linkedEvent.key !== event.key)
                .map((linkedEvent) => linkedEvent.key);
            const eventWasLinked = remainingEventKeys.length !== file.events.length;

            if (eventWasLinked) {
                const eventsObject = {};
                remainingEventKeys.forEach((key) => {
                    eventsObject[key] = true;
                });
                firebase
                    .database()
                    .ref("files")
                    .child(file.key)
                    .update({ events: eventsObject });
            }
        });

        if (calendar) {
            const eventsObject = {};
            (calendar.events || []).forEach((calendarEvent) => {
                if (calendarEvent.key !== event.key) {
                    eventsObject[calendarEvent.key] = true;
                }
            });
            firebase
                .database()
                .ref("calendars")
                .child(calendar.key)
                .update({ events: eventsObject });
        }

        firebase.database().ref("events").child(event.key).remove();
        setConfirmDeleteDialogOpen(false);
        onClose();
    };

    const handleCloseNewNameDialog = (newName, okPressed) => {
        const trimmedName = newName.trim();

        if (trimmedName && okPressed) {
            firebase
                .database()
                .ref("events")
                .child(event.key)
                .update({ name: trimmedName });
            event.name = trimmedName;
        }

        setNewNameDialogOpen(false);
    };

    const formattedDate = event?.date
        ? dayjs(event.date).format("dddd, D MMMM YYYY")
        : "Date not specified";

    return (
        <>
            <Dialog
                fullScreen
                onClose={onClose}
                aria-labelledby="view-event-title"
                open={open}
                TransitionComponent={Transition}
                slotProps={{
                    paper: {
                        sx: { backgroundColor: "background.default" },
                    },
                }}
            >
                {event && (
                    <Box sx={{ minHeight: "100%" }}>
                        <Box
                            component="header"
                            sx={{
                                position: "sticky",
                                top: 0,
                                zIndex: 10,
                                borderBottom: "1px solid",
                                borderColor: "divider",
                                backgroundColor: (theme) =>
                                    theme.palette.mode === "dark"
                                        ? "rgba(25, 32, 28, 0.96)"
                                        : "rgba(255, 255, 255, 0.96)",
                                backdropFilter: "blur(12px)",
                            }}
                        >
                            <Container
                                maxWidth="lg"
                                sx={{
                                    minHeight: 68,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: "text.secondary",
                                            fontWeight: 800,
                                            letterSpacing: "0.04em",
                                            textTransform: "uppercase",
                                        }}
                                    >
                                        Event details
                                    </Typography>
                                </Box>
                                {userCanEdit && (
                                    <>
                                        <Tooltip title="Rename event">
                                            <IconButton
                                                onClick={() =>
                                                    setNewNameDialogOpen(true)
                                                }
                                                aria-label="Rename event"
                                                sx={{
                                                    border: "1px solid",
                                                    borderColor: "divider",
                                                    borderRadius: 2,
                                                }}
                                            >
                                                <EditOutlinedIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete event">
                                            <IconButton
                                                onClick={() =>
                                                    setConfirmDeleteDialogOpen(
                                                        true,
                                                    )
                                                }
                                                aria-label="Delete event"
                                                sx={{
                                                    color: "error.main",
                                                    border:
                                                        "1px solid rgba(211, 47, 47, 0.18)",
                                                    borderRadius: 2,
                                                }}
                                            >
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </>
                                )}
                                <Tooltip title="Close">
                                    <IconButton
                                        onClick={onClose}
                                        aria-label="Close event"
                                        sx={{ ml: { xs: 0, sm: 0.5 } }}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </Tooltip>
                            </Container>
                        </Box>

                        <Box
                            sx={{
                                borderBottom: "1px solid",
                                borderColor: "divider",
                                background: (theme) =>
                                    theme.palette.mode === "dark"
                                        ? "linear-gradient(145deg, rgba(31, 184, 94, 0.15), rgba(25, 32, 28, 0.9) 58%, rgba(31, 184, 94, 0.06))"
                                        : "linear-gradient(145deg, rgba(0, 149, 61, 0.13), rgba(255, 255, 255, 0.82) 58%, rgba(0, 149, 61, 0.045))",
                            }}
                        >
                            <Container
                                maxWidth="lg"
                                sx={{ py: { xs: 4.5, sm: 6.5 } }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: { xs: "flex-start", sm: "center" },
                                        gap: { xs: 2, sm: 2.5 },
                                        flexDirection: { xs: "column", sm: "row" },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: { xs: 54, sm: 64 },
                                            height: { xs: 54, sm: 64 },
                                            borderRadius: 3,
                                            display: "grid",
                                            placeItems: "center",
                                            flexShrink: 0,
                                            color: "primary.main",
                                            backgroundColor:
                                                "background.paper",
                                            boxShadow:
                                                "0 8px 24px rgba(20, 35, 25, 0.09)",
                                        }}
                                    >
                                        <CalendarMonthOutlinedIcon
                                            sx={{ fontSize: { xs: 29, sm: 34 } }}
                                        />
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            id="view-event-title"
                                            variant="h3"
                                            component="h1"
                                            sx={{
                                                fontSize: {
                                                    xs: "2rem",
                                                    sm: "2.75rem",
                                                },
                                                lineHeight: 1.05,
                                                overflowWrap: "anywhere",
                                            }}
                                        >
                                            {event.name}
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                flexWrap: "wrap",
                                                gap: { xs: 1, sm: 2 },
                                                mt: 1.5,
                                                color: "text.secondary",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 0.65,
                                                }}
                                            >
                                                <CalendarMonthOutlinedIcon
                                                    sx={{ fontSize: 18 }}
                                                />
                                                <Typography variant="body2">
                                                    {formattedDate}
                                                </Typography>
                                            </Box>
                                            {calendar?.name && (
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 0.65,
                                                    }}
                                                >
                                                    <FolderCopyOutlinedIcon
                                                        sx={{ fontSize: 18 }}
                                                    />
                                                    <Typography variant="body2">
                                                        {calendar.name}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            </Container>
                        </Box>

                        <Container
                            maxWidth="lg"
                            component="main"
                            sx={{ py: { xs: 3.5, sm: 5 } }}
                        >
                            <FileListAccordion
                                files={linkedFiles}
                                fromFile={fromFile}
                            />
                        </Container>
                    </Box>
                )}
            </Dialog>

            <DeleteEventDialog
                event={event}
                open={confirmDeleteDialogOpen}
                onClose={() => setConfirmDeleteDialogOpen(false)}
                onConfirm={handleConfirmDelete}
            />
            <DialogWithTextInput
                open={newNameDialogOpen}
                onClose={handleCloseNewNameDialog}
                title="Rename event"
                message="Choose a clear name for this event."
                inputLabel="Event name"
                confirmLabel="Save name"
                initialValue={event?.name}
                headerIcon={<DriveFileRenameOutlineIcon />}
                modern
            />
        </>
    );
}
